'use client';

import { Badge } from '@/components/ui/data-display/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/data-display/hover-card';

type OutreachPriorityBadgeProps = {
  spotlightScore?: number | null;
  relevanceLikelihood?: number | null;
  responseLikelihood?: number | null;
  hiringImpactLikelihood?: number | null;
  label?: string;
  badgeClassName?: string;
  contentAlign?: 'center' | 'start' | 'end';
};

const normalizePercent = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
};

const computeFallbackPriority = (
  relevanceLikelihood: number | null,
  responseLikelihood: number | null,
  hiringImpactLikelihood: number | null
) => {
  if (
    relevanceLikelihood === null ||
    responseLikelihood === null ||
    hiringImpactLikelihood === null
  ) {
    return null;
  }
  return Math.round(
    relevanceLikelihood * 0.45 +
      responseLikelihood * 0.35 +
      hiringImpactLikelihood * 0.2
  );
};

export function OutreachPriorityBadge({
  spotlightScore,
  relevanceLikelihood,
  responseLikelihood,
  hiringImpactLikelihood,
  label = 'Outreach Priority',
  badgeClassName = 'text-2xs bg-amber-100 text-amber-900 px-1.5 py-0',
  contentAlign = 'start',
}: OutreachPriorityBadgeProps) {
  const relevance = normalizePercent(relevanceLikelihood);
  const response = normalizePercent(responseLikelihood);
  const hiringImpact = normalizePercent(hiringImpactLikelihood);
  const normalizedSpotlight = normalizePercent(spotlightScore);
  const fallbackPriority = computeFallbackPriority(relevance, response, hiringImpact);
  const displayPriority = normalizedSpotlight ?? fallbackPriority;
  const hasBreakdown =
    relevance !== null || response !== null || hiringImpact !== null;

  const badge = (
    <Badge variant="secondary" className={badgeClassName}>
      {label}
      {displayPriority !== null ? ` ${displayPriority}%` : ''}
    </Badge>
  );

  if (!hasBreakdown) {
    return badge;
  }

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="inline-flex cursor-pointer appearance-none border-0 bg-transparent p-0 text-left"
          aria-label="View outreach priority score breakdown"
        >
          {badge}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align={contentAlign} className="w-64 p-3">
        <div className="space-y-2">
          <div className="text-overline text-muted-foreground">
            Score Breakdown
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-caption">
            <span className="text-muted-foreground">Relevance likelihood</span>
            <span className="text-label text-foreground">
              {relevance !== null ? `${relevance}%` : '—'}
            </span>
            <span className="text-muted-foreground">Response likelihood</span>
            <span className="text-label text-foreground">
              {response !== null ? `${response}%` : '—'}
            </span>
            <span className="text-muted-foreground">Hiring impact likelihood</span>
            <span className="text-label text-foreground">
              {hiringImpact !== null ? `${hiringImpact}%` : '—'}
            </span>
          </div>
          <div className="text-caption text-muted-foreground">
            Weighted as 45% relevance, 35% response, 20% hiring impact.
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
